import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  Shield,
  BarChart3,
  Users,
  ArrowLeft,
} from "lucide-react";
import { logo, logoWhite } from "../assets";
import { getGlobalData } from "../api/strapi/utils";
import AdminLanguage from "../components/language/AdminLanguage";
import { loginUser, restoreAuthSession } from "../redux/actions/authActions";
import ForcePasswordChange from "../components/auth/ForcePasswordChange";

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "";

const resolveMediaUrl = (media) => {
  if (!media) return null;
  if (media.url) return `${STRAPI_URL}${media.url}`;
  if (media.data?.attributes?.url) return `${STRAPI_URL}${media.data.attributes.url}`;
  return null;
};

const LoginBootstrapLoader = ({ logoUrl, companyName, message }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#133618] via-[#15803D] to-[#16A34A] font-sans px-6 relative overflow-hidden">
    <div
      className="absolute inset-0 opacity-[0.07] pointer-events-none"
      style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }}
    />
    <div className="relative z-10 flex flex-col items-center text-center">
      <img
        src={logoUrl}
        alt={companyName}
        className="h-14 w-auto object-contain mb-8 drop-shadow-lg"
      />
      <div className="relative mb-6">
        <div className="w-14 h-14 rounded-full border-2 border-white/20" />
        <div className="absolute inset-0 w-14 h-14 rounded-full border-2 border-transparent border-t-white border-r-white/60 animate-spin" />
      </div>
      <p className="text-white font-display font-semibold text-lg tracking-tight mb-1">
        {companyName}
      </p>
      <p className="text-white/70 text-sm animate-pulse">{message}</p>
      <div className="mt-8 flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-white/50 animate-pulse"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </div>
    </div>
  </div>
);

const AdminLoginPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, error } = useSelector((state) => state.auth);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [showForceChange, setShowForceChange] = useState(false);
  const [suppressRedirect, setSuppressRedirect] = useState(false);

  const from = location.state?.from?.pathname || "/admin/dashboard";
  const companyName = globalData?.siteName || "Africa Systems";
  const logoDarkUrl = resolveMediaUrl(globalData?.logoWhite) || logoWhite;
  const logoLightUrl = resolveMediaUrl(globalData?.logo) || logo;

  const features = [
    { icon: Users, labelId: "admin.login.feature.users" },
    { icon: BarChart3, labelId: "admin.login.feature.reports" },
    { icon: Shield, labelId: "admin.login.feature.security" },
  ];

  useEffect(() => {
    const restore = async () => {
      await dispatch(restoreAuthSession());
      setSessionChecked(true);
    };
    restore();
  }, [dispatch]);

  useEffect(() => {
    const fetchGlobalData = async () => {
      const result = await getGlobalData();
      if (result.ok) {
        setGlobalData(result.data?.attributes || result.data);
      }
    };
    fetchGlobalData();
  }, []);

  useEffect(() => {
    if (sessionChecked && isAuthenticated && !suppressRedirect) {
      setRedirecting(true);
      navigate(from, { replace: true });
    }
  }, [sessionChecked, isAuthenticated, from, navigate, suppressRedirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuppressRedirect(true);
    if (password === "0000") setShowForceChange(true);
    const result = await dispatch(loginUser(identifier, password));
    if (result.ok) {
      if (result.user?.must_change_password || password === "0000") {
        setShowForceChange(true);
      } else {
        setSuppressRedirect(false);
        setRedirecting(true);
        navigate(from, { replace: true });
      }
    } else {
      setShowForceChange(false);
      setSuppressRedirect(false);
    }
  };

  if (!sessionChecked || redirecting || (isAuthenticated && !showForceChange)) {
    return (
      <LoginBootstrapLoader
        logoUrl={logoDarkUrl}
        companyName={companyName}
        message={intl.formatMessage({
          id: isAuthenticated || redirecting
            ? "admin.login.redirecting"
            : "admin.login.checkingSession",
        })}
      />
    );
  }

  return (
    <div className="min-h-screen flex font-sans">
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative overflow-hidden bg-gradient-to-br from-[#133618] via-[#15803D] to-[#16A34A]">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-accent-200/10 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full text-white">
          <div>
            <div className="flex items-center gap-3 mb-16">
              <img
                src={logoDarkUrl}
                alt={companyName}
                className="h-12 w-auto object-contain"
              />
              <div>
                <p className="font-display font-bold text-lg leading-tight tracking-tight">
                  {companyName}
                </p>
                <p className="text-white/60 text-xs tracking-widest uppercase">
                  {intl.formatMessage({ id: "admin.login.portal" })}
                </p>
              </div>
            </div>

            <h1 className="font-display text-3xl xl:text-4xl font-bold leading-tight mb-4">
              {intl.formatMessage({ id: "admin.login.hero.title" })}
              <br />
              <span className="text-accent-200">
                {intl.formatMessage({ id: "admin.login.hero.titleHighlight" })}
              </span>
            </h1>
            <p className="text-white/70 text-base leading-relaxed max-w-sm">
              {intl.formatMessage({ id: "admin.login.hero.description" })}
            </p>
          </div>

          <ul className="space-y-4">
            {features.map(({ icon: Icon, labelId }) => (
              <li key={labelId} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-accent-200" />
                </div>
                <span className="text-sm text-white/80">
                  {intl.formatMessage({ id: labelId })}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-white/40 text-xs">
            {intl.formatMessage(
              { id: "admin.login.copyright" },
              { year: new Date().getFullYear(), company: companyName }
            )}
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-neutral-50 relative">
        <ForcePasswordChange
          visible={showForceChange}
          onClose={() => setShowForceChange(false)}
          onSuccess={() => {
            setShowForceChange(false);
            setRedirecting(true);
            navigate(from, { replace: true });
          }}
        />

        {loading && (
          <div className="absolute inset-0 z-20 bg-neutral-50/70 backdrop-blur-[2px] flex flex-col items-center justify-center">
            <div className="relative mb-4">
              <div className="w-12 h-12 rounded-full border-2 border-light/20" />
              <div className="absolute inset-0 w-12 h-12 rounded-full border-2 border-transparent border-t-light border-r-light/50 animate-spin" />
            </div>
            <p className="text-sm font-medium text-neutral-600">
              {intl.formatMessage({ id: "admin.login.submitting" })}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-5 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-light transition-colors group"
          >
            <ArrowLeft
              size={16}
              className="transition-transform group-hover:-translate-x-0.5"
            />
            {intl.formatMessage({ id: "admin.login.backToSite" })}
          </Link>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 lg:hidden">
              <img
                src={logoLightUrl}
                alt={companyName}
                className="h-9 w-auto object-contain"
              />
            </div>
            <AdminLanguage />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className="w-full max-w-[420px]">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 bg-light/10 text-light-200 text-xs font-medium px-3 py-1.5 rounded-full mb-5 border border-light/20">
                <Lock size={12} />
                {intl.formatMessage({ id: "admin.login.secureAccess" })}
              </div>
              <h2 className="font-display text-2xl font-bold text-neutral-900 mb-2">
                {intl.formatMessage({ id: "admin.login.title" })}
              </h2>
              <p className="text-neutral-500 text-sm">
                {intl.formatMessage({ id: "admin.login.subtitle" })}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="identifier"
                  className="block text-sm font-medium text-neutral-700 mb-1.5"
                >
                  {intl.formatMessage({ id: "admin.login.identifier" })}
                </label>
                <div
                  className={`relative rounded-xl border bg-white transition-all duration-200 ${
                    focusedField === "identifier"
                      ? "border-light ring-2 ring-light/20 shadow-soft"
                      : "border-neutral-200 hover:border-neutral-300"
                  }`}
                >
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    id="identifier"
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    onFocus={() => setFocusedField("identifier")}
                    onBlur={() => setFocusedField(null)}
                    placeholder={intl.formatMessage({
                      id: "admin.login.identifierPlaceholder",
                    })}
                    required
                    disabled={loading}
                    autoComplete="username"
                    className="w-full pl-10 pr-4 py-3 bg-transparent rounded-xl outline-none text-sm text-neutral-900 placeholder:text-neutral-400 disabled:opacity-60"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-neutral-700 mb-1.5"
                >
                  {intl.formatMessage({ id: "admin.login.password" })}
                </label>
                <div
                  className={`relative rounded-xl border bg-white transition-all duration-200 ${
                    focusedField === "password"
                      ? "border-light ring-2 ring-light/20 shadow-soft"
                      : "border-neutral-200 hover:border-neutral-300"
                  }`}
                >
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField(null)}
                    placeholder={intl.formatMessage({
                      id: "admin.login.passwordPlaceholder",
                    })}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                    className="w-full pl-10 pr-11 py-3 bg-transparent rounded-xl outline-none text-sm text-neutral-900 placeholder:text-neutral-400 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-50"
                    aria-label={intl.formatMessage({
                      id: showPassword
                        ? "admin.login.hidePassword"
                        : "admin.login.showPassword",
                    })}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-600 text-xs font-bold">!</span>
                  </div>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-light hover:bg-light-200 active:bg-[#1a4a1f] text-white font-medium py-3 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-px active:translate-y-0"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    {intl.formatMessage({ id: "admin.login.submitting" })}
                  </>
                ) : (
                  intl.formatMessage({ id: "admin.login.submit" })
                )}
              </button>
            </form>

            <p className="text-center text-xs text-neutral-400 mt-8 leading-relaxed">
              {intl.formatMessage(
                { id: "admin.login.accessRestricted" },
                { company: companyName }
              )}
              <br />
              {intl.formatMessage({ id: "admin.login.contactAdmin" })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
